import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';
import ApiService from '../../services/api';

interface Category {
  id: number;
  name: string;
  displayName: string;
  icon: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  backgroundImage?: string;
  createdAt: string;
  updatedAt: string;
}

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    icon: '💅',
    description: '',
    displayOrder: 0
  });
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<File | null>(null);

  useEffect(() => {
    loadCategories();
    
    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };
    loadHomeSettings();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAdminCategories();
      setCategories(response.categories || []);
    } catch (error: any) {
      console.error('Categories load error:', error);
      setError('Kategoriler yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.displayName) {
      setError('Kategori adı ve görünen ad zorunludur');
      return;
    }

    try {
      setLoading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('displayName', formData.displayName);
      formDataToSend.append('icon', formData.icon);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('displayOrder', formData.displayOrder.toString());
      
      if (selectedBackgroundImage) {
        formDataToSend.append('backgroundImage', selectedBackgroundImage);
      }
      
      await ApiService.createCategory(formDataToSend);
      setShowCreateModal(false);
      setFormData({ name: '', displayName: '', icon: '💅', description: '', displayOrder: 0 });
      setSelectedBackgroundImage(null);
      await loadCategories();
      setError(null);
    } catch (error: any) {
      console.error('Create category error:', error);
      setError('Kategori oluşturulurken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingCategory || !formData.name || !formData.displayName) {
      setError('Kategori adı ve görünen ad zorunludur');
      return;
    }

    try {
      setLoading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('displayName', formData.displayName);
      formDataToSend.append('icon', formData.icon);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('displayOrder', formData.displayOrder.toString());
      
      if (selectedBackgroundImage) {
        formDataToSend.append('backgroundImage', selectedBackgroundImage);
      }
      
      await ApiService.updateCategory(editingCategory.id, formDataToSend);
      setEditingCategory(null);
      setFormData({ name: '', displayName: '', icon: '💅', description: '', displayOrder: 0 });
      setSelectedBackgroundImage(null);
      await loadCategories();
      setError(null);
    } catch (error: any) {
      console.error('Update category error:', error);
      setError('Kategori güncellenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.deleteCategory(id);
      await loadCategories();
      setError(null);
    } catch (error: any) {
      console.error('Delete category error:', error);
      setError('Kategori silinirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      setLoading(true);
      await ApiService.toggleCategoryStatus(id);
      await loadCategories();
      setError(null);
    } catch (error: any) {
      console.error('Toggle category error:', error);
      setError('Kategori durumu değiştirilirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      displayName: category.displayName,
      icon: category.icon,
      description: category.description || '',
      displayOrder: category.displayOrder
    });
    setSelectedBackgroundImage(null);
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setFormData({ name: '', displayName: '', icon: '💅', description: '', displayOrder: 0 });
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setFormData({ name: '', displayName: '', icon: '💅', description: '', displayOrder: 0 });
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setFormData({ name: '', displayName: '', icon: '💅', description: '', displayOrder: 0 });
  };

  if (loading && categories.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="h-16 bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-light text-white">Kategori Yönetimi</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kategori
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-900 border border-red-700 text-red-200 rounded">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-200 hover:text-red-100"
          >
            ×
          </button>
        </div>
      )}

      {/* Categories List */}
              <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  İkon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Kategori Adı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Görünen Ad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Sıra
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {categories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-2xl">
                    {category.icon}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{category.name}</div>
                    {category.description && (
                      <div className="text-sm text-gray-400">{category.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {category.displayName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {category.displayOrder}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      category.isActive 
                        ? 'bg-green-900 text-green-200' 
                        : 'bg-red-900 text-red-200'
                    }`}>
                      {category.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleStatus(category.id)}
                        className="text-gray-400 hover:text-white transition-colors"
                        title={category.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      >
                        {category.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => startEdit(category)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="p-6 rounded-lg border w-full max-w-md text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <h2 className="text-xl font-light text-white mb-4">Yeni Kategori Oluştur</h2>
            <form onSubmit={handleCreateCategory}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Kategori Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}
                    placeholder="manicure"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Görünen Ad *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    placeholder="MANİKÜR"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    İkon
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    placeholder="💅"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    rows={3}
                    placeholder="Kategori açıklaması..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Görüntüleme Sırası
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Arka Plan Resmi
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedBackgroundImage(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Bu resim kategori hizmet kartlarının arka planında görünecek
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition-colors"
                >
                  <X className="w-4 h-4 mr-2 inline" />
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-2 inline" />
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="p-6 rounded-lg border w-full max-w-md text-white" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <h2 className="text-xl font-light text-white mb-4">Kategori Düzenle</h2>
            <form onSubmit={handleUpdateCategory}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Kategori Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    placeholder="manicure"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Görünen Ad *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    placeholder="MANİKÜR"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    İkon
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    placeholder="💅"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    rows={3}
                    placeholder="Kategori açıklaması..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Görüntüleme Sırası
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Arka Plan Resmi
                  </label>
                  {editingCategory?.backgroundImage && (
                    <div className="mb-2">
                      <img
                        src={editingCategory.backgroundImage}
                        alt="Current background"
                        className="w-20 h-12 object-cover rounded border border-gray-600"
                      />
                      <p className="text-xs text-gray-400 mt-1">Mevcut resim</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedBackgroundImage(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded focus:outline-none focus:border-pink-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Bu resim kategori hizmet kartlarının arka planında görünecek
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 border border-gray-600 text-gray-300 rounded hover:bg-gray-800 transition-colors"
                >
                  <X className="w-4 h-4 mr-2 inline" />
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-2 inline" />
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
